import type {
  CameraConfiguration as CameraConfigurationProto,
  CameraIntrinsics as CameraIntrinsicsProto,
  ConfigOption,
  StreamInfo,
} from '@sb/messenger/auto_generated/messenger/camera_bot_service';

import type { CameraIntrinsicsExtended } from '../../../types/cameraTypes';

/**
 * Ensure ConfigOption has all required map fields populated for proto
 * serialization. Equipment configs persisted before a proto change (e.g.
 * `valueLabels` added by PR #15140) may be missing fields entirely; the
 * generated ts-proto encoder calls `Object.entries` on them unconditionally
 * and crashes with "Cannot convert undefined or null to object".
 */
export function normalizeConfigOption(opt: ConfigOption): ConfigOption {
  return { ...opt, valueLabels: opt.valueLabels ?? {} };
}

export function normalizeConfigOptions(
  options: Record<string, ConfigOption>,
): Record<string, ConfigOption> {
  const result: Record<string, ConfigOption> = {};

  for (const [key, opt] of Object.entries(options)) {
    result[key] = normalizeConfigOption(opt);
  }

  return result;
}

/**
 * Normalize a CameraConfiguration proto-shaped object before encoding.
 * Safe to call with `undefined`; returns `undefined` in that case so callers
 * can preserve their existing nullish-coalescing fallbacks.
 */
export function normalizeCameraConfigurationProto(
  config: CameraConfigurationProto | undefined,
): CameraConfigurationProto | undefined {
  if (config == null) return config;

  return {
    ...config,
    configOptions: normalizeConfigOptions(config.configOptions ?? {}),
  };
}

/**
 * Normalize camera-bot's observed `availableStreams` to their JSON
 * round-trip form.
 *
 * ts-proto decodes always materialize optional fields as present keys with
 * an explicit `undefined` value (e.g. `StreamInfo.depthMetrics: undefined`
 * for streams that don't carry depth metrics — true for exactly the
 * Insta360 COLOR_FRONT/COLOR_BACK streams). The equipment row's `config`
 * column is jsonb, and the write path serializes with `JSON.stringify`,
 * which silently drops undefined-valued keys. Without normalizing first,
 * an in-memory config with `{ depthMetrics: undefined }` would never
 * `lodash.isEqual` a DB-round-tripped config missing that key entirely —
 * a permanent, spurious mismatch.
 *
 * Call this on the observed streams before both storing them in-memory and
 * persisting them, so the in-memory and DB-round-tripped shapes always
 * agree.
 */
export function normalizeAvailableStreams(streams: StreamInfo[]): StreamInfo[] {
  return JSON.parse(JSON.stringify(streams)) as StreamInfo[];
}

/**
 * Convert camera-bot's self-reported intrinsics into the integration type,
 * carrying the lens distortion the camera reports.
 *
 * The distortion model and the coefficients travel together or not at all.
 * `deproject_pixels` (python/libs/geometry/plane_utils.py) keys entirely off
 * whether the coefficient list is non-empty: with coefficients but no model it
 * takes the `cv2.undistortPoints` branch, which *inverts* the polynomial. That
 * is the wrong direction for a librealsense `inverse_brown_conrady` camera, and
 * meaningless for one reporting `RS2_DISTORTION_NONE` — which camera-bot
 * surfaces as an absent model alongside a still-populated (all-zero)
 * coefficient array (`RealSenseCamera.cpp`). Dropping the coefficients when the
 * model is absent keeps that state unreachable, so an unmodelled camera
 * deprojects with a straight `K^-1` exactly as it did before.
 */
export function builtInIntrinsicsFromProto(
  data: CameraIntrinsicsProto,
): CameraIntrinsicsExtended {
  const distortionModel = data.distortionModel ?? null;

  return {
    width: data.width,
    height: data.height,
    fx: data.fx,
    fy: data.fy,
    ppx: data.ppx,
    ppy: data.ppy,
    distortionCoefficients: distortionModel
      ? data.distortionCoefficients ?? []
      : [],
    distortionModel,
  };
}
